import React, { useState } from 'react';
import { Mail, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import axios from 'axios';

const API = `${window.location.origin}/api`;

/**
 * Public waitlist signup form for the paid Full History Report (currently disabled).
 * Props:
 *   - source: string tag saved to DB (e.g. 'website', 'results', 'account')
 *   - compact: boolean — show a tighter version for inline cards
 */
function WaitlistForm({ source = 'website', compact = false }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }
    if (!consent) {
      setError('Please tick the consent box so we can email you.');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/reports/waitlist`, {
        email: email.trim(),
        name: name.trim() || null,
        consent,
        source,
      });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        className="flex items-start gap-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700/40 rounded-lg p-4"
        data-testid="waitlist-success"
      >
        <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-200">You're on the waitlist</p>
          <p className="text-xs text-emerald-700 dark:text-emerald-300 mt-0.5">
            We'll email you the moment the full history check launches.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="waitlist-form">
      {!compact && (
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1" htmlFor="waitlist-name">
            Name (optional)
          </label>
          <Input
            id="waitlist-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            disabled={loading}
            className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700"
            data-testid="waitlist-name-input"
          />
        </div>
      )}

      <div>
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1" htmlFor="waitlist-email">
          Email <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <Input
            id="waitlist-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            disabled={loading}
            required
            className="pl-9 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700"
            data-testid="waitlist-email-input"
          />
        </div>
      </div>

      <label className="flex items-start gap-2 cursor-pointer select-none">
        <Checkbox
          checked={consent}
          onCheckedChange={(v) => setConsent(!!v)}
          disabled={loading}
          className="mt-0.5"
          data-testid="waitlist-consent-checkbox"
        />
        <span className="text-xs text-gray-600 dark:text-gray-400">
          Email me when this launches. I understand I can unsubscribe at any time.
        </span>
      </label>

      {error && (
        <p className="text-xs text-red-600 dark:text-red-400" data-testid="waitlist-error">
          {error}
        </p>
      )}

      <Button
        type="submit"
        disabled={loading}
        className="w-full bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-semibold"
        data-testid="waitlist-submit-btn"
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Joining…
          </>
        ) : (
          'Notify me at launch'
        )}
      </Button>

      <p className="text-[11px] text-gray-500 dark:text-gray-500 leading-relaxed">
        We'll only email you about the full history check launch. No spam. One-click unsubscribe in every email.
      </p>
    </form>
  );
}

export default WaitlistForm;
